"use client";

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";

export interface OpportunityContactRoleData {
  Id: string;
  Role: string | null;
  IsPrimary: boolean;
  Contact: {
    Name: string;
    Email: string;
  };
}

export interface salesforceOpportunityAll {
  ContactRoles?: OpportunityContactRoleData[];
  sf_opportunity_name:string;
}

export interface FetchedSalesforceQuote {
  Id?: string;
  Name?: string;
  SBQQ__Opportunity2__c?: string;
  SBQQ__PrimaryContact__r?: {
    Email: string;
  };
  DDC_Quote_Status__c?: boolean;
  CreatedBy?: {
    Email: string;
  }
  LastModifiedDate?: string;
}

export interface GlobalContextType {
  message: string;
  setMessage: (message: string) => void;
  clearMessage: () => void;

  fetchedSalesforceQuote: FetchedSalesforceQuote | null;
  setFetchedSalesforceQuote: (data: FetchedSalesforceQuote | null) => void;

  fetchedSalesforceOpportunity: salesforceOpportunityAll | null;
  setFetchedSalesforceOpportunity: (data: salesforceOpportunityAll | null) => void;
}

const GlobalDataContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalDataProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessageState] = useState<string>("");

  const [fetchedSalesforceQuote, setFetchedSalesforceQuote] = useState<FetchedSalesforceQuote | null>(null);
  const [fetchedSalesforceOpportunity, setFetchedSalesforceOpportunity] = useState<salesforceOpportunityAll | null>(null);

  const setMessage = (message: string) => {
    setMessageState(message);
  };

  const clearMessage = () => {
    setMessageState("");
  };

  const value = useMemo(
    () => ({
      message,
      setMessage,
      clearMessage,
      fetchedSalesforceQuote,
      setFetchedSalesforceQuote,
      fetchedSalesforceOpportunity, 
      setFetchedSalesforceOpportunity,
    }),
    [message, fetchedSalesforceQuote, fetchedSalesforceOpportunity]
  );

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
};

export const useGlobalData = (): GlobalContextType => {
  const context = useContext(GlobalDataContext);
  if (!context) {
    throw new Error("useGlobalData must be used within a GlobalDataProvider");
  }
  return context;
};